import { Button, Divider, Skeleton, Stack, Typography } from '@mui/material'
import { CautionText, SkeletonText } from 'utoniq-ui'

import {
  EventStatus,
  Fee,
  Maybe,
  PaymentDetailFragment,
  PreSaleLotteryDetailFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

import { LotteryContentCell, MembershipCell } from './components'
import {
  ApplyPreSaleLotteryValue,
  LotteryDetailForm,
} from './LotteryDetailForm'

export * from './fragments'

export type State = {
  isRootAuthFailed: boolean
  loading: boolean
  isAppliedUser: boolean
  paymentSource?: Maybe<PaymentDetailFragment> | undefined
  originFullNameValue?: string
  preSale: Maybe<PreSaleLotteryDetailFragment> | undefined
  paymentFees?: Maybe<Fee[]> | undefined
  choices:
    | { itemId: string; skuId: string | undefined; count: number }[]
    | undefined
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onSubmit: (values: ApplyPreSaleLotteryValue | undefined) => Promise<void>
  onReadyPaymentMethod: (
    values: ApplyPreSaleLotteryValue | undefined
  ) => Promise<void>
}

export const LotteryDetailTemplate: Template<State, Action> = ({
  state,
  onSubmit,
  onReadyPaymentMethod,
}) => {
  const { t } = useTranslation('lotteryDetail')
  const { routerNames, routerPages, generatePath } = useRouter()

  return (
    <BaseLayout
      margin
      header={{
        back: true,
        defaultBackPage:
          state.preSale?.event?.status === EventStatus.Published
            ? generatePath(routerPages.ShokufesEventDetail, {
                eventId: state.preSale?.event?.id ?? '#',
              })
            : undefined,
        title: t('title', '先行販売情報詳細'),
      }}
    >
      <Head title={routerNames.LotteryDetail} />
      {state.loading || !state.preSale ? (
        <>
          <LotteryTitleSkeletonCell />
          <LotteryContentSkeletonCell />
        </>
      ) : (
        <>
          <LotteryTitleCell title={state.preSale.title} />
          {state.isAppliedUser && (
            <LotteryAppliedUserCell preSaleId={state.preSale.id} />
          )}
          <LotteryContentCell preSale={state.preSale} />
          {state.preSale.isPremium && (
            <MembershipCell preSale={state.preSale} />
          )}
          <LotteryDetailForm
            preSale={state.preSale}
            originFullNameValue={state.originFullNameValue}
            paymentSource={state.paymentSource}
            onSubmit={onSubmit}
            onReadyPaymentMethod={onReadyPaymentMethod}
            paymentFees={state.paymentFees}
            choices={state.choices}
            isAppliedUser={state.isAppliedUser}
          />
        </>
      )}
    </BaseLayout>
  )
}

type LotteryTitleCellProps = {
  title: string
}
const LotteryTitleCell: Cell<LotteryTitleCellProps> = ({ title }) => {
  return (
    <CellFrame sx={{ pt: 3, pb: 5 }}>
      <Typography variant="h4">{title}</Typography>
    </CellFrame>
  )
}

type LotteryAppliedUserCellProps = {
  preSaleId: string
}
const LotteryAppliedUserCell: Cell<LotteryAppliedUserCellProps> = ({
  preSaleId,
}) => {
  const { t } = useTranslation('lotteryDetail')
  const { generatePath, routerPages } = useRouter()
  return (
    <CellFrame>
      <CautionText type="error">{t('lotteryAppliedUserNote')}</CautionText>
      <Stack sx={{ py: 5 }} alignItems="center">
        <Button
          size="large"
          sx={{ width: 212 }}
          href={generatePath(routerPages.LotteryApplicationDetail, {
            preSaleId,
          })}
        >
          {t('lotteryApplicationDetail', '抽選応募詳細を確認する')}
        </Button>
      </Stack>
    </CellFrame>
  )
}

const LotteryTitleSkeletonCell: Cell = () => {
  return (
    <CellFrame sx={{ pt: 3, pb: 5 }}>
      <SkeletonText variant="h4" width="50%" />
    </CellFrame>
  )
}

const LotteryContentSkeletonCell: Cell = () => {
  return (
    <CellFrame>
      <Stack sx={{ pb: 5 }}>
        <Stack direction="row" spacing={1}>
          <Stack width="10%">
            <SkeletonText variant="body2" />
          </Stack>
          <Stack flex={1}>
            <SkeletonText variant="body2" />
          </Stack>
        </Stack>
        <Stack sx={{ pt: 2 }} spacing={1}>
          <SkeletonText numberOfLine={3} variant="body2" />
        </Stack>
        <Stack sx={{ py: 2 }} spacing={1}>
          <SkeletonText numberOfLine={2} variant="body2" />
        </Stack>
        <Stack sx={{ py: 2 }} spacing={1}>
          <SkeletonText numberOfLine={2} variant="body2" />
        </Stack>
        <Stack sx={{ py: 2 }} spacing={1}>
          <SkeletonText numberOfLine={2} variant="body2" />
        </Stack>
      </Stack>
      <Stack sx={{ pb: 5 }}>
        <Stack sx={{ py: 1, bgcolor: 'grey.50' }}>
          <SkeletonText variant="h4" />
        </Stack>
        <Stack
          sx={{
            borderColor: 'grey.100',
            borderWidth: 1,
            borderStyle: 'solid',
            borderRadius: 2,
            px: 4,
            py: 5,
            my: 4,
          }}
          spacing={1}
        >
          <SkeletonText variant="caption" width="25%" />
          <SkeletonText variant="caption" width="50%" />
        </Stack>
      </Stack>
      <Stack sx={{ pb: 5 }}>
        <Stack sx={{ py: 1, bgcolor: 'grey.50' }}>
          <SkeletonText variant="h4" />
        </Stack>
        <Stack direction="row" spacing={3} alignItems="center" sx={{ py: 5 }}>
          <Stack flex={1}>
            <Skeleton variant="rectangular" width="100%" height={40} />
          </Stack>
          <Stack flex={1}>
            <SkeletonText variant="caption" width="50%" />
          </Stack>
        </Stack>
        <Divider />
      </Stack>
      {Array.from(Array(2).keys()).map(key => (
        <Stack sx={{ pb: 5, pt: 3 }} key={key}>
          <Stack sx={{ py: 1, bgcolor: 'grey.50' }}>
            <SkeletonText variant="h4" />
          </Stack>
          <Stack spacing={3}>
            <SkeletonText variant="body2" numberOfLine={3} />
          </Stack>
        </Stack>
      ))}
      <Stack flex={1} sx={{ py: 4 }} alignItems="center">
        <Skeleton
          variant="rectangular"
          width={244}
          height={44}
          sx={{ borderRadius: '34px' }}
        />
      </Stack>
    </CellFrame>
  )
}
